import logo from 'assets/header-prevencor-xx.png';
import AssinaturaEletronica from 'components/core/AssinaturaEletronica';
import LaudoActionButtons from 'components/laudos/LaudoActionButtons';
import { Card, Form, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { selectErgoById } from 'services/ergo';
import useSWR from 'swr';
import { generateErgoPdf } from './generateErgoPdf';

const LEADS_13 = [
  'MC5m',
  'D1m',
  'D2m',
  'D3m',
  'AVRm',
  'AVLm',
  'AVFm',
  'V1m',
  'V2m',
  'V3m',
  'V4m',
  'V5m',
  'V6m',
];
const LEADS_EXTRA = ['V3R', 'V4R', 'V5R'];
const ALL_LEADS = [...LEADS_13, ...LEADS_EXTRA];

function ListSection({ title, items = [], keyName = 'DESCRICAO' }) {
  if (!items?.length) return null;
  return (
    <Form.Group className="mb-2">
      <u>
        <strong>{title}:</strong>
      </u>
      {items.map((item, idx) => (
        <div key={idx} className="ms-2">
          <strong>{item[keyName] || item.DESCRICAO || item.FRASE || ''}</strong>
        </div>
      ))}
    </Form.Group>
  );
}

export default function ReportErgo() {
  const { id } = useParams();
  const {
    data: ergo,
    isLoading,
    error,
  } = useSWR(id ? ['ergo', id] : null, () => selectErgoById(id));

  const ecgRow = Array.isArray(ergo?.alteracoesECG) ? ergo.alteracoesECG[0] || {} : {};

  const hasExtraLeads = LEADS_EXTRA.some(
    (lead) => ecgRow[`${lead}A`] || ecgRow[`${lead}E`] || ecgRow[`${lead}D`]
  );
  const canais = hasExtraLeads ? 16 : 13;
  const leadsToShow = hasExtraLeads ? ALL_LEADS : LEADS_13;

  return (
    <Card className="mb-2 mt-2">
      <Card.Header className="text-center">
        <img
          src={logo}
          alt="Prevencor"
          style={{ display: 'block', maxWidth: '100%', margin: '0 auto 8px' }}
          className="no-print"
        />
        <div>
          <u>
            <strong>Teste Ergométrico em Esteira Rolante</strong>
          </u>
        </div>
        <div>
          <u>
            <strong>Monitorização em {canais} Canais</strong>
          </u>
        </div>
      </Card.Header>

      {isLoading && (
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Card.Body>
      )}

      {error && !isLoading && (
        <Card.Body className="text-center text-danger py-5">
          {error.message || 'Erro ao carregar o laudo.'}
        </Card.Body>
      )}

      {ergo && !isLoading && !error && (
        <>
          <Card.Body>
            <strong>
              Paciente: {ergo.CODPAC} {ergo.NOMEPAC}
            </strong>
            <br />
            <strong>Sexo: </strong>
            {ergo.SEXOPAC}&nbsp;&nbsp;&nbsp;
            <strong>Idade: </strong>
            {ergo.idadePAC} anos&nbsp;&nbsp;&nbsp;
            <strong>Data do Laudo: </strong>
            {ergo.dataErgo || ergo.dataErgoFmt || '-'}
            <br />
            <div className="d-flex justify-content-between">
              <span>
                <strong>Médico que Realizou: </strong>Dr.(ª): {ergo.Realizou}
              </span>
              <strong>{ergo.CODEXAME}</strong>
            </div>
            <strong>Médico que Solicitou: </strong>Dr.(ª): {ergo.Solicitou}
            <br />
            <strong>Convênio: </strong>
            {ergo.SIGLACONV}
            <hr />
            <ListSection title="Indicações do Teste" items={ergo.indicacoes} />
            <ListSection title="Queixas" items={ergo.queixas} keyName="FRASE" />
            <ListSection title="Medicamentos" items={ergo.medicamentos} keyName="FRASE" />
            <ListSection title="Fatores de Risco" items={ergo.fatoresRisco} />
            <ListSection title="Atividade Física" items={ergo.atividadesFisica} />
            {ergo.EXAMEFISICO && (
              <Form.Group className="mb-2">
                <u>
                  <strong>Exame Físico:</strong>
                </u>
                <div className="ms-2">
                  <strong>{ergo.EXAMEFISICO}</strong>
                </div>
              </Form.Group>
            )}
            <ListSection title="Eletrocardiograma Basal" items={ergo.ecgBasal} keyName="FRASE" />
            <div style={{ pageBreakBefore: 'always' }}>
              <Form.Group className="mb-2">
                <u>
                  <strong>Alterações Eletrocardiográficas:</strong>
                </u>
                {leadsToShow.map((lead) => {
                  const a = ecgRow[`${lead}A`];
                  const e = ecgRow[`${lead}E`];
                  const d = ecgRow[`${lead}D`];
                  return (
                    <div key={lead} className="d-flex mb-1 ms-1">
                      <strong style={{ minWidth: '52px' }}>{lead}:</strong>
                      <div>
                        <div>
                          <strong>ANTES..............:</strong> {a || ''}
                        </div>
                        <div>
                          <strong>NO ESFORÇO...:</strong> {e || ''}
                        </div>
                        <div>
                          <strong>APÓS................:</strong> {d || ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Form.Group>
            </div>
            <div style={{ pageBreakBefore: 'always' }}>
              <ListSection title="Arritmias" items={ergo.arritmias} />
              <ListSection title="Comportamento da PA" items={ergo.comportamentoPA} />

              <Form.Group className="mb-2">
                <u>
                  <strong>Comentários:</strong>
                </u>
                {ergo.PADEITADO && (
                  <div className="ms-2">Índice Cronotrópico: {ergo.PADEITADO}%</div>
                )}
                {(ergo.comentarios || []).map((c, idx) => (
                  <div key={idx} className="ms-2">
                    <strong>{c.DESCRICAO || ''}</strong>
                  </div>
                ))}
              </Form.Group>

              <Form.Group className="mb-2">
                <u>
                  <strong>Observações:</strong>
                </u>
                {ergo.OBS
                  ? String(ergo.OBS)
                      .split('\n')
                      .map((line, idx) => (
                        <div key={idx} className="ms-2">
                          {line}
                        </div>
                      ))
                  : null}
              </Form.Group>

              <Form.Group className="mb-2">
                <u>
                  <strong>Conclusões:</strong>
                </u>
                {ergo.PAHIPER && <div className="ms-2">Escore de Duke: {ergo.PAHIPER}</div>}
                {(ergo.conclusoes || []).map((c, idx) => (
                  <div key={idx} className="ms-2">
                    <strong>{c.DESCRICAO || ''}</strong>
                  </div>
                ))}
              </Form.Group>

              <Card className="mt-3 text-center">
                <Card.Header>
                  <Card.Text>
                    Obs - O Resultado do presente laudo não configura necessariamente a presença ou
                    ausência de doença, devendo obrigatoriamente ser correlacionado com demais dados
                    clínicos e exames complementares pertinentes ao caso.
                  </Card.Text>
                </Card.Header>
              </Card>
            </div>
          </Card.Body>

          <Card.Footer className="text-center no-print">
            <LaudoActionButtons
              actions={[
                {
                  key: 'timbrado',
                  label: 'Timbrado',
                  onClick: () => generateErgoPdf({ ergo }),
                },
              ]}
            />
          </Card.Footer>

          <Card.Footer className="text-center">
            <AssinaturaEletronica data={ergo} nome={ergo.Realizou} showPrintButton={false} />
          </Card.Footer>
        </>
      )}
    </Card>
  );
}
